import { useNotification } from 'admin';
import { type FullBuyerMaterialSpecification, isFieldErrorResponse, MaterialComponentCategory } from 'api';
import { convertFromMaterialComponentValue, convertToMaterialComponentInput } from 'features';
import { Form, useForm, yupResolver } from 'form';
import { useNavigate } from 'react-router-dom';
import { Box, Button } from 'ui';
import { DeleteOutlined } from 'ui/icons';

import { useDeleteBuyerMaterialSpecification } from '../../hooks/useDeleteBuyerMaterialSpecification/useDeleteBuyerMaterialSpecification';
import { useUpdateBuyerMaterialSpecification } from '../../hooks/useUpdateBuyerMaterialSpecification/useUpdateBuyerMaterialSpecification';
import { type BuyerMaterialSpecificationFormType, BuyerMaterialSpecificationSchema } from '../../pages/schema';
import { BuyerMaterialSpecificationFormFields } from '../BuyerMaterialSpecificationFormFields/BuyerMaterialSpecificationFormFields';

type Props = {
  buyerMaterialSpecificationId: number;
  buyerMaterialSpecificationData: FullBuyerMaterialSpecification;
};

export const BuyerMaterialSpecificationUpdateForm = ({
  buyerMaterialSpecificationId,
  buyerMaterialSpecificationData,
}: Props) => {
  const navigate = useNavigate();
  const { open } = useNotification();
  const form = useForm<BuyerMaterialSpecificationFormType>({
    resolver: yupResolver(BuyerMaterialSpecificationSchema),
    values: {
      buyer_id: buyerMaterialSpecificationData.buyer.id,
      file_url: buyerMaterialSpecificationData.file_url ?? undefined,
      material_colour_id: buyerMaterialSpecificationData.material_colour?.id ?? undefined,
      material_form_id: buyerMaterialSpecificationData.material_form.id,
      material_grade_id: buyerMaterialSpecificationData.material_grade.id,
      material_id: buyerMaterialSpecificationData.material.id,
      material_qualities: convertFromMaterialComponentValue(
        buyerMaterialSpecificationData.material_component_values,
        MaterialComponentCategory.MATERIAL_QUALITY
      ),
      notes: buyerMaterialSpecificationData.notes || undefined,
      packaging_specification_ids: buyerMaterialSpecificationData.packaging_specifications.map(
        (packagingSpecification) => packagingSpecification.id
      ),
    },
  });

  const onUpdateError = (error: Error) => {
    if (isFieldErrorResponse(error)) {
      for (const field of Object.keys(error.response?.data)) {
        form.setError(field as keyof BuyerMaterialSpecificationFormType, { message: error.response.data[field] });
      }
    } else {
      open?.({
        message: `Failed to update material spec: ${error}`,
        type: 'error',
      });
    }
  };
  const { updateBuyerMaterialSpecification, isPending } = useUpdateBuyerMaterialSpecification({
    onError: onUpdateError,
  });
  const { mutate: deleteBuyerMaterialSpecification } = useDeleteBuyerMaterialSpecification();
  const onUpdate = async (values: BuyerMaterialSpecificationFormType) => {
    const materialComponents = convertToMaterialComponentInput(values.material_qualities);

    await updateBuyerMaterialSpecification({
      buyerId: values.buyer_id,
      buyerMaterialSpecificationId,
      document: values.document ?? undefined,
      materialColourId: values.material_colour_id,
      materialComponents,
      materialFormId: values.material_form_id,
      materialGradeId: values.material_grade_id,
      materialId: values.material_id,
      notes: values.notes,
      packagingSpecificationIds: values.packaging_specification_ids,
    });
  };

  const onDelete = () => {
    deleteBuyerMaterialSpecification({ buyerMaterialSpecificationId: Number(buyerMaterialSpecificationId) });
  };

  return (
    <Form {...form} onSubmit={onUpdate}>
      <BuyerMaterialSpecificationFormFields />
      <Box display="flex" justifyContent="space-between">
        <Button onClick={onDelete} variant="secondary" startIcon={<DeleteOutlined />}>
          Delete
        </Button>
        <Box display="flex" gap={2}>
          <Button onClick={() => navigate(-1)} variant="secondary">
            Cancel
          </Button>
          <Button loading={isPending} type="submit">
            Update
          </Button>
        </Box>
      </Box>
    </Form>
  );
};
